<template>
    <q-card>
        <q-expansion-item
            dense
            header-class="bg-secondary text-white"
            expand-icon-class="text-white"
            v-model="open"
            ref="expansion"
            @before-show="validateIdentifier(workItem.identifiers[0], workItem.identifiers[0].value)"
        >
            <template #header>
                <q-tooltip v-if="error">
                    {{ error.error }}
                </q-tooltip>
                <q-separator 
                    vertical
                    style="width: 5px;position: absolute;left: 0;top: 0;bottom: 0;"
                    :color="error ? 'negative' : ($q.dark.isActive ? 'dark' : 'white')"
                />
                <q-item-section avatar>
                    <q-item-label>
                        <q-badge v-if="!workItem.id" color="green-13" class="text-bold">
                            New
                        </q-badge>
                        <q-badge v-else color="amber-10" class="text-bold">
                            Editing
                        </q-badge>
                    </q-item-label>
                    <q-item-label v-if="error">
                        <q-badge color="negative">
                            Error(s)
                        </q-badge>
                    </q-item-label>
                </q-item-section>
                <q-item-section>
                    <q-item-label class="text-bold">
                        {{ workItem.work_type?.name ?? '*No Work Type*' }} <span class="text-grey-4" style="vertical-align: middle;font-size: smaller;" v-if="workItem.id">(ID:{{ workItem.id }})</span>{{ 
                            $q.screen.lt.md ? '' : (' - ' + (workItem?.sub_customer?.contact?.org_name ?? workItem.customer?.contact?.org_name ?? 'Unknown')) 
                        }}
                    </q-item-label>
                    <q-item-label v-if="$q.screen.lt.md">
                        {{ workItem.sub_customer?.contact?.org_name ?? workItem.customer?.contact?.org_name ?? 'Unknown' }}
                    </q-item-label>
                </q-item-section>
                <q-item-section v-if="$q.screen.gt.sm" class="q-gutter-sm">
                    <q-item-label class="text-bold">
                        <!-- Identifiers -->
                        <q-chip
                            square
                            size="0.65rem"
                            v-for="row in workItem.identifiers"
                            :icon="row.type?.icon ?? undefined"
                            :color="row.type[$q.dark.isActive ? 'color' : 'color_dark'] ?? 'accent'"
                            :text-color="isDarkRadial(getCssVar(row.type[$q.dark.isActive ? 'color' : 'color_dark'] ?? 'accent'), 'white', 'black', 175)"
                            class="q-mr-xs"
                        >
                            {{ truncateString(row.value) }}

                            <q-tooltip
                                :class="[
                                    `bg-${row.type[$q.dark.isActive ? 'color' : 'color_dark'] ?? 'accent'}`,
                                ]"
                                :style="'color: ' + isDarkRadial(getCssVar(row.type[$q.dark.isActive ? 'color' : 'color_dark'] ?? 'accent'), 'white', 'black', 175)"
                            >
                                {{ row.type.title }}
                            </q-tooltip>
                        </q-chip>
                        <!-- Values -->
                        <q-chip
                            square
                            size="0.65rem"
                            v-for="row in workItem.values"
                            :icon="row.type.icon"
                            :color="$q.dark.isActive ? row.type.color_dark : row.type.color"
                            :style="`color: ${calculateDarkOrLight($q.dark.isActive ? row.type.color_dark : row.type.color, 'white', 'black')}`"
                            :class="{ hidden: row.typed <= 0, 'q-mr-xs': 1 }"
                        >
                            {{ row.text ?? processText(row) ?? row.value }}

                            <q-tooltip
                                :class="['bg-' + ($q.dark.isActive ? row.type.color_dark : row.type.color), 'text-white']"
                            >
                                {{ row.type.title }}
                            </q-tooltip>
                        </q-chip>
                    </q-item-label>
                </q-item-section>
                <q-item-section 
                    side
                    class="text-white"
                >
                    <q-item-label class="flex justify-center" style="align-items: center;cursor: default;font-size: 0.875rem;flex-direction: row;height: 100%;">
                        <q-icon name="mdi-account-hard-hat" style="margin-right:3px;" size="0.875rem" /> {{ workItem.pay?.length ?? '0' }}
                        <q-icon 
                            size="0.5rem"
                            class="q-pl-xs"
                            :style="`color: ${calculateGradientColor(workItem.total_labor_split, 0, 100)};`"
                            :name="`mdi-circle-slice-${Math.floor(8*((workItem.total_labor_split / 100) - 0.12) / 0.76) - 1}`"
                        />
                        <q-tooltip>
                            {{ workItem.pay.length }} worker{{ workItem.pay.length != 1 ? 's' : '' }} with {{ workItem.total_labor_split }}% allocation.
                        </q-tooltip>
                    </q-item-label>
                    <q-item-label>&nbsp;</q-item-label>
                </q-item-section>
                <q-item-section v-if="workItem.total_lumper_pay != null && $q.screen.gt.md" :style="(workItem.billing_pay != null && $q.screen.gt.md) ? 'padding-right: 2.5px !important;' : ''" side>
                    <q-item-label>
                        <q-badge text-color="black" color="white">
                            {{ workItem.total_lumper_pay == null ? '$0.00' : toUSD(workItem.total_lumper_pay) }}
                            
                            <q-tooltip>
                                Total amount paid to workers
                            </q-tooltip>
                        </q-badge>
                    </q-item-label>
                </q-item-section>
                <q-item-section v-if="workItem.billing_pay" :style="(workItem.total_lumper_pay != null && $q.screen.gt.md) ? 'padding-left: 2.5px !important;' : ''" side>
                    <q-item-label v-if="workItem.total_lumper_pay && $q.screen.lt.lg">
                        <q-badge text-color="black" color="white">
                            {{ workItem.total_lumper_pay == null ? '$0.00' : toUSD(workItem.total_lumper_pay) }}
                            
                            <q-tooltip>
                                Total amount paid to workers
                            </q-tooltip>
                        </q-badge>
                    </q-item-label>
                    <q-item-label class="row" style="align-items:center;">
                        <q-badge
                            color="green-13"
                            :text-color="isDarkRadial('#00e676', 'white', 'black')"
                        >
                            {{ workItem.billing_pay == null ? '$0.00' : toUSD(workItem.billing_pay) }}
                            
                            <q-tooltip class="bg-positive text-white">
                                Total amount billed to the customers
                            </q-tooltip>
                        </q-badge>
                    </q-item-label>
                </q-item-section>
                <!-- <q-item-section side>
                    <q-btn
                        dense
                        color="positive"
                        icon="mdi-content-save"
                        tabindex="0"
                        @click.stop="$emit('save', workItem)"
                    />
                </q-item-section> -->
                <!-- <q-item-section side>
                    <q-btn
                        dense
                        color="negative"
                        icon="mdi-close"
                        @click.stop="$emit('cancel', workItem)"
                    />
                </q-item-section> -->
            </template>
            
            <div class="q-px-md q-pt-sm">

                <q-item v-if="!workItem.pay.length && false">
                    <q-item-section side>
                        <q-icon name="mdi-alert-circle" class="text-red" />
                    </q-item-section>
                    <q-item-section>
                        <q-item-label class="text-red" @click="openWorkerSplit">
                            Add Workers
                        </q-item-label>
                    </q-item-section>
                </q-item>

                <q-item class="q-pl-xs" v-if="workItem.id ?? false" dense>
                    <q-item-section>
                        <q-item-label class="q-gutter-sm">
                            <!-- <q-btn
                                no-caps
                                :label="(workItem.notes ? 'View' : 'Add') + ' Notes'"
                                color="grey-8"
                                size="sm"
                                :icon="'mdi-note-' + (workItem.notes ? 'search' : 'plus')"
                                @click.prevent
                            /> -->

                            <!-- <q-btn
                                no-caps
                                label="Change Customer & Work Type"
                                color="purple-13"
                                size="sm"
                                icon="mdi-swap-horizontal"
                                @click="swap = true"
                            /> -->

                            <q-btn
                                label="Change Worker Splts"
                                color="primary"
                                size="sm"
                                icon="mdi-percent"
                                @click="openWorkerSplit"
                            />
                        </q-item-label>
                    </q-item-section>

                    <template v-if="workItem.id ?? false">
                        <q-item-section avatar>
                            <q-item-label caption>
                                Created At
                            </q-item-label>
                            <q-item-label>
                                {{ format(workItem.created_at) }}
                            </q-item-label>

                            <template v-if="$q.screen.lt.lg && (workItem.updated_at != workItem.created_at)">
                                <q-item-label caption>
                                    Updated At
                                </q-item-label>
                                <q-item-label>
                                    {{ format(workItem.updated_at) }}
                                </q-item-label>
                            </template>
                        </q-item-section>

                        <template v-if="$q.screen.gt.md && (workItem.updated_at != workItem.created_at)">
                            <q-separator class="q-ml-md" vertical />

                            <q-item-section avatar>
                                <q-item-label caption>
                                    Updated At
                                </q-item-label>
                                <q-item-label>
                                    {{ format(workItem.updated_at) }}
                                </q-item-label>
                            </q-item-section>
                        </template>
                    </template>
                </q-item>
            </div>

            <div class="row">
                <q-card flat class="col-12">
                    <q-card-section class="q-pa-xs">
                        <q-list dense class="q-my-sm">
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <q-select
                                            dense
                                            square
                                            filled
                                            emit-value
                                            map-options
                                            color="secondary"
                                            label="Customer"
                                            v-model="workItem.sub_customer_id"
                                            :options="customerOptions"
                                            @update:model-value="workItem.work_type_id = null"
                                            options-selected-class="text-green-13"
                                        >
                                            <template #option="scope">
                                                <q-item v-bind="scope.itemProps">
                                                    <q-item-section side>
                                                        <q-icon 
                                                            name="mdi-check"
                                                            color="green-13"
                                                            :style="!scope.selected ? 'opacity: 0;' : ''"
                                                        />
                                                    </q-item-section>
                                                    <q-item-section>
                                                        <q-item-label>{{ scope.opt.label }}</q-item-label>
                                                    </q-item-section>
                                                    <q-item-section side>
                                                        <q-item-label>
                                                            <q-badge 
                                                                :color="scope.opt.value ? 'deep-orange' : 'primary'"
                                                                :label="scope.opt.value ? 'Sub Customer' : 'Main Customer'"
                                                            />
                                                        </q-item-label>
                                                    </q-item-section>
                                                </q-item>
                                            </template>
                                        </q-select>
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <q-select
                                            emit-value
                                            map-options
                                            filled
                                            square
                                            dense
                                            :options="workTypeOptions"
                                            v-model="workItem.work_type_id"
                                            label="Work Type"
                                            color="secondary"
                                        />
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            
                            <!-- Identifiers -->
                            <template v-for="(identifier, y) in workItem.identifiers">
                                <q-item v-if="identifier.type">
                                    <q-item-section>
                                        <q-input
                                            dense
                                            filled
                                            square
                                            :label="identifier.type.title"
                                            :color="$q.dark.isActive ? 'grey-2' : 'grey-8'"
                                            v-model="identifier.value"
                                            @update:model-value="validateIdentifier(identifier, identifier.value)"
                                            @change="validateIdentifier(identifier, identifier.value)"
                                            @keyup.ctrl.enter="$emit('save', workItem)"
                                            :maxlength="identifier.type.max_length"
                                            :error="!!identifierErrors[identifier.identifier_type_id]"
                                            :hide-bottom-space="!identifierErrors[identifier.identifier_type_id]"
                                            :error-message="identifierErrors[identifier.identifier_type_id]"
                                            no-error-icon
                                        >
                                            <template #prepend>
                                                <q-icon size="xs" :name="identifier.type.icon" color="accent" />
                                            </template>

                                            <template #append>
                                                <q-chip
                                                    square
                                                    text-color="white"
                                                    :color="identifierValidated[identifier.identifier_type_id] ? 'positive': 'negative'"
                                                    :icon="identifierValidated[identifier.identifier_type_id] ? 'mdi-check' : 'mdi-alert'"
                                                >
                                                    {{
                                                        identifierValidated[identifier.identifier_type_id]
                                                            ? 'Valid' 
                                                            : 'Invalid' 
                                                    }}
                                                    <q-tooltip 
                                                        :class="`bg-${identifierValidated[identifier.identifier_type_id] ? 'positive' : 'negative'} text-white`"
                                                    >
                                                        {{ identifierValidated[identifier.identifier_type_id] ? 'This identifier is valid' : 'This identifier is invalid' }}
                                                    </q-tooltip>

                                                    <q-inner-loading :showing="identifierChecking">
                                                        <q-spinner size="1.55em" color="secondary" />
                                                    </q-inner-loading>
                                                </q-chip>
                                            </template>
                                        </q-input>
                                    </q-item-section>
                                    <q-item-section class="full-height" side>
                                        <q-select
                                            dense
                                            filled
                                            square
                                            emit-value
                                            map-options
                                            :options="identifierTypes.map(v => ({label: v.title, value: v.id}))"
                                            :model-value="identifier.identifier_type_id"
                                            @update:model-value="setIdentifierType($event)"
                                            :class="{ 'q-mb-md': !!identifierErrors[identifier.identifier_type_id] }"
                                        />
                                    </q-item-section>
                                </q-item>
                            </template>
                            <!-- Values -->
                            <template v-for="(row, y) in workItem.values">
                                <q-item>
                                    <q-item-section>
                                        <q-input
                                            dense
                                            filled
                                            square
                                            type="number"
                                            inputmode="numeric"
                                            :label="row.type.title"
                                            :model-value="row.value"
                                            :error="valueError(row.type) ? true : null"
                                            :error-message="error?.details[row.type.id]?.message ?? undefined"
                                            :color="$q.dark.isActive ? row.type.color_dark : row.type.color"
                                            @focus="$event.target.select()"
                                            @keyup.ctrl.enter="$emit('save', workItem)"
                                            @update:model-value="($event) => row.value = (!$event.length ? 0 : $event)"
                                        >
                                            <template #prepend>
                                                <q-icon
                                                    size="xs"
                                                    :name="row.type.icon" 
                                                    :color="$q.dark.isActive ? row.type.color_dark : row.type.color"
                                                />
                                            </template>
                                        </q-input>
                                    </q-item-section>
                                    <q-item-section v-if="false" side>
                                        <q-btn
                                            dense
                                            icon="mdi-eye-off"
                                            @click="showing = showing.filter(v => v.id !== row.bracket_type_id)"
                                        >
                                            <q-tooltip>
                                                Hide {{ row.label }}
                                            </q-tooltip>
                                        </q-btn>
                                    </q-item-section>
                                </q-item>
                            </template>

                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <q-input
                                            dense
                                            filled
                                            square
                                            counter
                                            label="Notes"
                                            color="secondary"
                                            v-model="workItem.notes"
                                            icon="mdi-note-text-outline"
                                            maxlength="255"
                                        >
                                            <template #prepend>
                                                <q-icon
                                                    size="xs"
                                                    name="mdi-note-text-outline"
                                                    color="secondary"
                                                />
                                            </template>
                                        </q-input>
                                    </q-item-label>
                                </q-item-section>
                            </q-item>

                            <q-expansion-item
                                :expand-icon-toggle="true"
                                :expand-separator="true"
                                expand-icon-class="hidden"
                                default-opened
                            >
                                <template #header>
                                    <q-item-section>
                                        <q-item-label
                                            :class="{
                                                'text-center text-bold': true,
                                                'text-red': unallocatedSum > 0, 
                                                'text-positive': unallocatedSum == 0
                                            }"
                                        >
                                            <q-icon name="mdi-account-hard-hat" />
                                            {{ workItem.pay.length }} worker{{ workItem.pay.length != 1 ? 's' : '' }} assigned
                                            <q-icon
                                                :name="(unallocatedSum > 0) ? 'mdi-alert-circle' : 'mdi-check-circle'"
                                            />
                                        </q-item-label>
                                        <q-item-label style="display: flex;overflow: hidden;align-items: center;">
                                            <span
                                                v-for="(pay, index) in workItem.pay"
                                                :style="`display:inline-flex;background-color: ${
                                                    backgroundColor(pay.worker)
                                                };width: ${pay.split}%;height: 35px;justify-content: center;align-items: center;`"
                                            >
                                                <avatar size="1.8em" :ref="`worker-${index}`" :user="pay.worker" />
                                                <q-tooltip
                                                    :style="`background-color: ${
                                                        backgroundColor(pay.worker)
                                                    };`"
                                                    :class="textColor(pay.worker)"
                                                >
                                                    {{ pay.worker.name }}
                                                    <div v-if="pay.worker.employee_id" class="text-caption">
                                                        EID: {{ pay.worker.employee_id }}
                                                    </div>
                                                    <div class="text-caption">
                                                        Split: {{ pay.split }}%
                                                    </div>
                                                </q-tooltip>
                                            </span>
                                            <span 
                                                :style="`display:${
                                                    unallocatedSum 
                                                        ? 'inline-flex' 
                                                        : 'none'
                                                };width: ${
                                                    unallocatedSum
                                                }%;height: 35px;justify-content: center;align-items: center;`"
                                                class="missing-allocation"
                                            >
                                                <q-tooltip :class="{
                                                        'text-center': true,
                                                        'bg-red': unallocatedSum > 0,
                                                        'bg-positive': unallocatedSum == 0
                                                    }"
                                                >
                                                    {{ 
                                                        unallocatedSum 
                                                            ? 'Missing Allocation' 
                                                            : 'All Workers Allocated' 
                                                    }}
                                                    <div class="text-caption">
                                                        {{ unallocatedSum }}% Unallocated
                                                    </div>
                                                </q-tooltip>
                                            </span>
                                        </q-item-label>
                                    </q-item-section>
                                </template>

                                <q-list separator>
                                    <q-item
                                        v-for="(pay, index) in workItem.pay"
                                        :key="index"
                                        clickable
                                        @click="openWorkerSplit"
                                    >
                                        <q-item-section side>
                                            <avatar size="1.8rem" :user="pay.worker" />
                                        </q-item-section>
                                        <q-item-section>
                                            {{ pay.worker.name }}
                                        </q-item-section>
                                        <q-item-section side>
                                            <q-item-label>
                                                {{ pay.split }}%
                                            </q-item-label>
                                            <q-item-label>
                                                Clocked {{ timeDiff(pay.time_start, pay.time_end) ?? 0 }} hours
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>

                                    <q-item 
                                        v-if="!workItem.pay.length"
                                        clickable
                                        @click="openWorkerSplit"
                                    >
                                        <q-item-section class="text-center text-red text-bold">
                                            <q-item-label>
                                                <q-icon name="mdi-alert-circle" />
                                                Add Workers
                                            </q-item-label>
                                            <q-item-label>
                                                Click here to add workers
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>
                                </q-list>
                            </q-expansion-item>
                        </q-list>
                    </q-card-section>

                    <q-card-actions class="q-pt-none q-px-md">
                        <!-- <q-btn
                            size="sm"
                            :label="'Validate ' + (!workItem.id ? 'new' : 'old') + ' values'"
                            icon="mdi-content-save"
                            color="positive"
                            @click="validateValues()"
                        /> -->
                        <q-btn
                            size="sm"
                            :label="!workItem.id ? 'Create' : 'Save'"
                            icon="mdi-content-save"
                            color="positive"
                            @click="$emit('save', workItem)"
                            :disable="identifierErrors.length || !workItem.identifiers[0].value.length"
                            v-if="!disableCreate"
                        />

                        <q-btn
                            size="sm"
                            label="Save All"
                            color="positive"
                            icon="mdi-content-save-all"
                            @click="$emit('save-all')"
                        />

                        <template v-if="workItem.id">
                            <q-btn
                                size="sm"
                                :disable="!workItem.isDirty"
                                label="Reset"
                                :color="$q.dark.isActive ? 'secondary':'primary'"
                                @click="$emit('cancel', workItem)" 
                            />


                            <q-btn
                                size="sm"
                                label="Delete"
                                icon="mdi-delete"
                                color="negative"
                                @click="$emit('delete', workItem.id)"
                            />
                        </template>

                        <q-btn
                            v-else
                            size="sm"
                            label="Cancel"
                            color="red-8"
                            @click="$emit('cancel', workItem)"
                        />
                    </q-card-actions>
                </q-card>
            </div>
        </q-expansion-item>

        <WorkerSplitDialog 
            v-model="workerSplit.open"
            v-model:pay="workItem.pay"
            :customer-id="customer.id"
            :work-item-id="workItem.id ?? NaN"
            :work-start-at="workStartAt"
        >
            <template #title>
                Worker Split
                <q-item-label :class="{'text-grey-4': $q.dark.isActive}" caption>
                    {{ workItem.work_type?.name ?? '' }}<span v-if="workItem.id"> (ID: {{ workItem.id }})</span> {{ 
                        $q.screen.lt.md ? '' : (' - ' + (workItem?.sub_customer?.contact?.org_name ?? workItem.customer?.contact?.org_name ?? 'Unknown')) 
                    }}
                </q-item-label>
            </template>
        </WorkerSplitDialog>
    </q-card>
</template>

<script>
import Avatar from '@/Components/Avatar.vue';
import WorkerSplitDialog from '../WorkerSplitDialog/WorkerSplitDialog.vue';

import { debounce } from 'quasar';

export default {
    name: 'WorkItemEdit',
    emits: ['cancel', 'save', 'save-all', 'delete', 'update:modelValue'],
    components: {
        Avatar,
        WorkerSplitDialog,
    },
    props: {
        customer: {
            type: Object,
            required: true
        },
        disableCreate: {
            type: Boolean,
            default: false
        },
        enableCrud: {
            type: Boolean,
            default: false
        },
        modelValue: {
            type: Object,
            required: true
        },
        error: {
            type: Object,
            default: () => null
        },
        identifierTypes: {
            type: Array,
            default: () => []
        },
        workStartAt: {
            type: String,
            required: true
        },
    },
    data() {
        return {
            open: (this.workItem?.id ?? false) || false,
            tab: 'customer',
            showing: [],
            swap: false,
            workerSplit: {
                open: false,
                cache: [],
            },
            identifier: ((this.workItem?.other?.length ?? false) ? 'other' : 'container_number'),
            workItem: this.modelValue,
            identifierErrors: [],
            identifierValidated: [],
            identifierChecking: false,
            valuesChecking: false,
            valuesErrors: null,
        };
    },
    methods: {
        openWorkerSplit() {
            this.workerSplit.open = true;
        },
        handleCustomerChange(subCustomer) {
            this.workItem.sub_customer = subCustomer;
            this.workItem.sub_customer_id = subCustomer?.id ?? null;
        },
        cancel() {
            this.$emit('cancel', this.modelValue);
        },
        showUseable() {
            const useableBrackets = this.getUsableBrackets();
            // Filter out any values that are not in the useable brackets
            this.workItem.values = this.workItem.values.filter(v => useableBrackets.find(b => b.bracket_type_id == v.bracket_type_id));
            // Add any new brackets
            useableBrackets.forEach((v) => {
                if (!this.workItem.values.find(b => b.bracket_type_id == v.bracket_type_id)) {
                    this.workItem.values.push(v);
                }
            });
        },
        getUsableBrackets() {
            let useableBrackets = [];

            if (this.workItem.sub_customer_id) {
                useableBrackets = 
                    this.customer.subCustomers.find(v => v.id == this.workItem.sub_customer_id)
                        .work_types.find(v => v.id == this.workItem.work_type_id)?.bracket_types ?? [];
            } else {
                useableBrackets = 
                    this.customer.workTypes.find(v => v.id == this.workItem.work_type_id)?.bracket_types ?? [];
            }

            return useableBrackets.map(v => ({
                bracket_type_id: v.id,
                type: v,
                value: 0,
            })) ?? [];
        },
        validateIdentifier(identifier, value) {
            if (!this.open) return;
            // Clear errors
            this.identifierErrors = [];
            // Clear validated
            this.identifierValidated = [];
            // Wait till they input something
            if (!value.length) return;

            if (!identifier.type) {
                console.error('Identifier Type not found');
                return;
            }

            if (identifier.type.force_capitalize) {
                this.workItem.identifiers[0].value = value.toUpperCase();
            }
            // Show loading state
            this.identifierChecking = true;
            // If min_length is set, check if value is less than min_length
            if (identifier.type.min_length && value.length < identifier.type.min_length) {
                this.identifierErrors[identifier.type.id] = `The "${identifier.type.title}" is too short`;
            }
            // If max_length is set, check if value is greater than max_length
            if (identifier.type.max_length && value.length > identifier.type.max_length) {
                this.identifierErrors[identifier.type.id] = `The "${identifier.type.title}" is too long`;
            }
            // If regex is set, check if value matches regex
            if (identifier.type.regex && !new RegExp(identifier.type.regex).test(value)) {
                this.identifierErrors[identifier.type.id] = `The "${identifier.type.title}" is improperly formatted.`;
                if (identifier.type.example) {
                    this.identifierErrors[identifier.type.id] += ` Example: ${identifier.type.example}`;
                }
            }
            // If there are errors, we'll stop here
            if (this.identifierErrors[identifier.type.id]) {
                this.identifierChecking = false;
                return;
            }
            // If it passes all checks, we'll try the duplicate check
            this.axios.get(route('api.v1.identifier-types.validate.identifier', { 
                identifierType: identifier.type.id, 
                identifier: identifier?.id ?? undefined
            }), {
                params: {
                    value,
                }
            })
                .then(({ data }) => {
                    if (!data.success) {
                        this.identifierErrors[identifier.type.id] = 
                            data?.message ?? error ?? 'There was an error validating the identifier';
                        return;
                    }

                    this.identifierValidated[identifier.type.id] = true;

                    if (data.message) {
                        debounce(() => {
                            this.$q.notify({
                                message: data.message,
                                color: 'positive',
                                icon: 'mdi-thumb-up',
                            });
                        }, 500);
                    }
                })
                .catch((error) => {
                    this.identifierErrors[identifier.type.id] =
                        error.response?.data?.message ?? error ?? 'There was an error validating the identifier';
                })
                .finally(() => {
                    this.identifierChecking = false;
                });
        },
        setIdentifierType(identifierTypeId) {
            const identifierType = this.identifierTypes.find(v => v.id == identifierTypeId);

            this.workItem.identifiers[0].identifier_type_id = identifierTypeId;
            this.workItem.identifiers[0].type = identifierType;

            this.validateIdentifier(this.workItem.identifiers[0], this.workItem.identifiers[0].value);
        },
        validateValues() {
            this.valuesErrors = null;
            this.valuesChecking = true;

            this.axios.post(route('api.v1.work-items.validate', this.workItem?.id ?? undefined), {
                values: this.workItem.values,
            })
                .then(({ data }) => {
                    if (data.message) {
                        debounce(() => {
                            this.$q.notify({
                                message: data.message,
                                color: 'positive',
                                icon: 'mdi-thumb-up',
                            });
                        }, 500);
                    }
                })
                .catch(({response}) => {
                    this.valuesErrors = response?.data?.data ?? 'There was an error validating the values';

                    this.$q.notify({
                        message: response?.data?.message ?? 'There was an error validating the values',
                        color: 'negative',
                        icon: 'mdi-thumb-down',
                    });
                })
                .finally(() => {
                    this.valuesChecking = false;
                });
        },
        processText(valueObj) {
            const field = valueObj.type;

            if (!field) return valueObj.value;

            const value = valueObj?.value ?? 0;
            const padding = value != 1 ? field.unit_multiple : field.unit;

            let padded = value;

            if (field?.unit_location == 'prefix') {
                padded = `${padding} ${padded}`;
            }

            if (field?.unit_location == 'suffix') {
                padded = `${padded} ${padding}`;
            }

            if (field?.unit_location == 'both') {
                padded = `${padding} ${padded} ${padding}`;
            }

            return padded;
        },
        nameAcronym(user) {
            let name = user?.name ?? '';
            let nonce = user?.employee_id ?? user?.id ?? 0;

            nonce += Math.floor(new Date(user?.created_at ?? 0).getTime() / 1000);

            if (typeof name === 'string') {
                const split = name.split(' ');
                name = {
                    id: nonce,
                    first_name: split[0].trim(),
                    last_name: split[split.length - 1]?.trim() ?? '',
                };
            }

            return name;
        },
        backgroundColor(user) {
            const {id, first_name, last_name} = this.nameAcronym(user);

            return this.stringToHexColor(id + first_name + last_name);
        },
        textColor(user) {
            const bg = this.backgroundColor(user) ?? false;

            if (!bg) return 'text-white';

            return this.isDarkRadial(bg, 'text-grey-1', 'text-grey-10');
        },
        valueError(type) {
            if (!this.error?.details || !this.error?.details[type.id]) {
                return false;
            }

            return this.error?.details[type.id]?.valid === false;
        },
        timeDiff(startTime, endTime) {
            const start = new Date(startTime);
            start.setSeconds(0);
            start.setMilliseconds(0);

            const end = new Date(endTime);
            end.setSeconds(0);
            end.setMilliseconds(0);
            
            // Compute the difference in milliseconds
            const diffMillis = end.getTime() - start.getTime();

            // Convert to hours with explicit rounding
            const diffInHours = Math.round((diffMillis / 1000 / 60 / 60) * 100) / 100;

            return diffInHours.toFixed(2); // Return as a string with 2 decimal places
        }
    },
    mounted() {
        // console.log(this.customer);
        this.$nextTick(() => {
            if (this.workItem.identifiers) {
                this.workItem.identifiers.forEach((identifier) => {
                    this.validateIdentifier(identifier, identifier.value);
                });
            }

            if (this.workItem.work_type_id) {
                this.showUseable();
            }
        });
    },
    computed: {
        editable() {
            return this.enableCrud && !this.locked;
        },
        customerOptions() {
            return [
                {
                    label: this.customer.contact.org_name,
                    value: null,
                },
                ...(this.customer.subCustomers ?? []).map(v => ({ 
                    label: v.contact.org_name, 
                    customer: v,
                    value: v.id 
                }))
            ];
        },
        workTypeOptions() {
            if (this.workItem.sub_customer_id) {
                return this.customer.subCustomers.find(v => v.id == this.workItem.sub_customer_id)
                    .work_types.map(v => ({ 
                        label: v.name, 
                        value: v.id,
                        workType: v,
                    })) ?? [];
            }

            return this.customer.workTypes.map(v => ({ label: v.name, value: v.id, workType: v, })) ?? [];
        },
        unallocatedSum() {
            return this.workItem.pay.reduce((acc, pay) => acc - pay.split, 100);
        },
        isOpen() {
            return !this.workItem.id;
        },
    },
    watch: {
        'modelValue.total_lumper_pay'(to) {
            this.workItem.total_lumper_pay = to;
        },
        'modelValue.billing_pay'(to) {
            this.workItem.billing_pay = to;
        },
        'modelValue': {
            handler(to) {
                this.workItem = to;
            },
            deep: true,
        },
        'workItem':{
            handler(to) {
                this.$emit('update:modelValue', to);
            },
            deep: true,
        },
        'workItem.work_type_id'(to) {
            this.showUseable();
        },
        open(val) {
            if (val) {

            }
        }
    },
}
</script>

<style lang="scss" scoped>
.missing-allocation {
    height: 35px;
    background: repeating-linear-gradient(
        /* Angle of the stripes */
        -45deg,
        /* Light red */
        $grey-12,
        /* Stripe width */
        $grey-12 5px,
        /* Starting point of dark red */
        $negative 5px,
        /* Ending point of dark red */
        $negative 10px
    );
    width: auto;
    border:1px solid $negative;
    border-radius: 0 5px 5px 0;
}
</style>